/* eslint-disable react/jsx-fragments */
/** @jsx jsx */
import { jsx } from '@emotion/react';

import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import toPairs from 'lodash/toPairs';
import omitBy from 'lodash/omitBy';
import trim from 'lodash/trim';
import { withTranslation } from 'react-i18next';

import Form from '../../form/form';
import getElementOptions from '../../elements/element-util/get-element-options';
import {
  defineFloat,
  objectMap,
  stringToBool,
  pipe,
  removeElementSelection,
  saveElementSelection,
} from '../../../util';

import makeSchema from './page-form.schema';
import { makeFormValidator } from '../../../util/validator';

// TODO: implement back-end support for previous page and uncomment.
import {
  ELEMENT_TYPE__BOOLEAN_INPUT,
  ELEMENT_TYPE__CHECKBOX_INPUT,
  ELEMENT_TYPE__EMAIL_INPUT,
  ELEMENT_TYPE__EMAIL_VERIFICATION_INPUT,
  ELEMENT_TYPE__IMAGE_MULTI_SELECT_INPUT,
  ELEMENT_TYPE__MEDIA_INPUT,
  ELEMENT_TYPE__NUMBER_INPUT,
  ELEMENT_TYPE__PHONE_NUMBER_INPUT,
  ELEMENT_TYPE__RANKING_IMAGE_INPUT,
  ELEMENT_TYPE__RANKING_INPUT,
  ELEMENT_TYPE__SLIDE_INPUT,
  PAGES__SHAPE_ITEM,
  PARTICIPANT__SHAPE_ITEM,
  // PAGES__SHAPE_ARRAY,
  RESULT__ITEM_SHAPE,
  SURVEY_CLIENT__SHAPE_ITEM,
} from '../../../redux/constants';
import { emailPromptPrefix } from '../../elements/element/social-input/get-email-prompt-id';
import { BottomNav } from '../bottom-nav/bottom-nav';

const parseSimpleValue = (value) => {
  try {
    const jsValue = JSON.parse(value);
    if (typeof jsValue === 'object' && 'other' in jsValue) {
      return jsValue;
    }
    return value;
  } catch (e) {
    return value;
  }
};

const parseCheckboxValues = (answers) => answers.map(parseSimpleValue);

const parseElementValue = (rawValue, element) => {
  switch (element.type) {
    case ELEMENT_TYPE__CHECKBOX_INPUT:
    case ELEMENT_TYPE__IMAGE_MULTI_SELECT_INPUT: {
      const checkboxValue = parseCheckboxValues(rawValue);
      return JSON.stringify(checkboxValue);
    }
    default: {
      const simpleValue = parseSimpleValue(rawValue);
      return JSON.stringify(simpleValue);
    }
  }
};

const prevalidateElement = (value, element) => {
  const {
    type,
    properties: { otherOption },
  } = element || { properties: {} };
  switch (type) {
    case ELEMENT_TYPE__BOOLEAN_INPUT:
      return stringToBool(value);
    case ELEMENT_TYPE__EMAIL_INPUT:
    case ELEMENT_TYPE__EMAIL_VERIFICATION_INPUT:
      return trim(value);
    case ELEMENT_TYPE__CHECKBOX_INPUT:
    case ELEMENT_TYPE__IMAGE_MULTI_SELECT_INPUT: {
      const arrayValue = Array.isArray(value) ? value : [value];
      const hasOtherValue = arrayValue.find((currentValue) => currentValue === '') === undefined;
      if (otherOption && !hasOtherValue) return {};
      return arrayValue;
    }
    case ELEMENT_TYPE__NUMBER_INPUT:
    case ELEMENT_TYPE__MEDIA_INPUT:
    case ELEMENT_TYPE__SLIDE_INPUT:
      return defineFloat(value);
    case ELEMENT_TYPE__PHONE_NUMBER_INPUT:
      return value === 'IVALID_PHONE_NUMBER' ? new Error(value) : value;
    case ELEMENT_TYPE__RANKING_IMAGE_INPUT:
    case ELEMENT_TYPE__RANKING_INPUT:
      return value && JSON.parse(value);
    default:
      return value;
  }
};

export const makePrevalidator = (selectElement) => (form) => {
  const nextForm = objectMap(form, (result, elementId, value) => {
    if (elementId.startsWith(emailPromptPrefix)) return result;
    return {
      ...result,
      [elementId]: prevalidateElement(value, selectElement(elementId)),
    };
  });
  return nextForm;
};

export const makeValidator = pipe(makeSchema, makeFormValidator);

const mapAnswers = (answers) => answers.map(({ elementId }) => elementId);
const removeSavedValues = (elementIds) => elementIds.forEach(removeElementSelection);

export class PageFormClass extends Component {
  state = { hasErrors: false };
  static propTypes = {
    answersBusy: PropTypes.bool,
    editable: PropTypes.bool,
    hasAnswers: PropTypes.bool,
    isPreview: PropTypes.bool,
    isTestMode: PropTypes.bool,
    page: PAGES__SHAPE_ITEM.isRequired,
    // pages: PAGES__SHAPE_ARRAY.isRequired,
    participant: PARTICIPANT__SHAPE_ITEM,
    result: RESULT__ITEM_SHAPE,
    selectElement: PropTypes.func.isRequired,
    selectElementsByParentId: PropTypes.func.isRequired,
    sendUpload: PropTypes.func,
    surveyClient: SURVEY_CLIENT__SHAPE_ITEM,
    submitAnswers: PropTypes.func,
  };
  static childContextTypes = {
    editable: PropTypes.bool,
    page: PAGES__SHAPE_ITEM,
  };
  static defaultProps = {
    answersBusy: false,
    editable: false,
    hasAnswers: false,
    isPreview: false,
    isTestMode: false,
    participant: null,
    result: undefined,
    sendUpload: undefined,
    surveyClient: undefined,
    submitAnswers: undefined,
  };

  getChildContext() {
    const { editable, page } = this.props;
    return { editable, page };
  }
  componentDidMount() {
    this.forceUpdate();
  }

  getElementDisplayOrder(elementId) {
    const element = this.props.selectElement(elementId);
    return getElementOptions(element, this.props.result);
  }

  elements = () => {
    const { page, selectElementsByParentId } = this.props;
    return selectElementsByParentId(page.canvas?.id);
  };
  isDisabled = () => {
    const { editable, page } = this.props;
    return editable || (page.terminating && !page.hasInputElements);
  };

  sendUploads = (form) => {
    return Promise.all(
      toPairs(form).reduce((acc, [elementId, value]) => {
        const element = this.props.selectElement(elementId);
        if (!element) return acc;
        const job = element.upload
          ? this.props.sendUpload(element).then(({ id }) => ({ element, value: id }))
          : Promise.resolve({ element, value });
        return [...acc, job];
      }, []),
    );
  };
  serializeUploadResults = (uploadResults) =>
    uploadResults.map(({ element, value }) => ({
      displayOrder: this.getElementDisplayOrder(element.id),
      elementId: element.id,
      value: parseElementValue(value, element, this.props),
    }));

  sendAnswers = (answers, event) => {
    const { hasAnswers, page, surveyClient, submitAnswers } = this.props;
    return submitAnswers({
      answers,
      event,
      hasAnswers,
      page,
      token: surveyClient.token,
    }).then(() => answers);
  };

  handleFormSubmit = (formWithOverloads, event) => {
    if (this.isDisabled()) {
      return;
    }
    const form = omitBy(formWithOverloads, (value, key) => key.startsWith(emailPromptPrefix));
    this.sendUploads(form)
      .then(this.serializeUploadResults)
      .then((answers) => this.sendAnswers(answers, event))
      .then(mapAnswers)
      .then(removeSavedValues)
      .catch(() => {});
  };

  handleFormChange = (form) => {
    toPairs(form).forEach(([key, value]) => saveElementSelection(key, value));
  };

  handleValidationError = ([error]) => {
    this.setState({ hasErrors: !!error });
    if (!error) return;
    const element = document.getElementById(error.property);
    if (element) {
      const index = this.elements().findIndex(({ id }) => id === error.property);
      const block = index === 0 ? 'end' : 'start';
      element.scrollIntoView({ behavior: 'smooth', block });
    }
  };

  render = () => {
    const { t } = this.props;
    const submitError = get(this.props, 'surveyClient.answersSubmitError');

    if (!this.props.editable) {
      return (
        <Form
          onFormChange={this.handleFormChange}
          onFormSubmit={this.handleFormSubmit}
          onValidationError={this.handleValidationError}
          prevalidate={makePrevalidator(this.props.selectElement)}
          validate={makeValidator({
            elements: this.elements(),
            isPreview: this.props.isPreview,
            isTestMode: this.props.isTestMode,
            participant: this.props.participant,
          })}
        >
          {this.props.children}
          <BottomNav {...this.props} answersDisabled={this.isDisabled()}>
            {this.state.hasErrors && (
              <div className="content-page__bage">{t('pages.completeAllQuestions')}</div>
            )}
            {!this.state.hasErrors && submitError && (
              <div className="content-page__bage">
                {submitError.message || t('pages.answersSubmitFailed')}
              </div>
            )}
          </BottomNav>
        </Form>
      );
    }

    return (
      <Fragment>
        {this.props.children}
        <BottomNav {...this.props} answersDisabled={this.isDisabled()}>
          {this.state.hasErrors && (
            <div className="content-page__bage">{t('pages.completeAllQuestions')}</div>
          )}
        </BottomNav>
      </Fragment>
    );
  };
}

export const PageForm = withTranslation()(PageFormClass);
